import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Search, Package, RefreshCw, Eye, Send, Trash2, Clock, CheckCircle, XCircle, AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { stockRequestsAPI } from '@/services/api';
import { formatDate, formatDateOnly } from '@/utils/dateFormat';

const statusConfig = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700', icon: Clock },
  pending: { label: 'Pending Review', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: XCircle },
  processing: { label: 'Processing', color: 'bg-blue-100 text-blue-700', icon: RefreshCw },
  completed: { label: 'Completed', color: 'bg-purple-100 text-purple-700', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-500', icon: XCircle }
};

const StockRequestListPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 1 });

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: pagination.page, page_size: 20 };
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      
      const data = await stockRequestsAPI.list(params);
      setRequests(data.items || []);
      setPagination({
        page: data.page,
        total: data.total,
        total_pages: data.total_pages
      });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load stock requests', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [pagination.page, statusFilter, toast]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleSubmit = async (requestId) => {
    try {
      await stockRequestsAPI.submit(requestId);
      toast({ title: 'Success', description: 'Request submitted to HQ' });
      fetchRequests();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to submit', variant: 'destructive' });
    }
  };

  const handleDelete = async (requestId) => {
    if (!window.confirm('Are you sure you want to cancel this request?')) return;
    
    try {
      await stockRequestsAPI.delete(requestId);
      toast({ title: 'Success', description: 'Request cancelled' });
      fetchRequests();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to cancel', variant: 'destructive' });
    }
  };

  const filteredRequests = requests.filter(req => 
    req.request_number?.toLowerCase().includes(search.toLowerCase()) ||
    req.items?.some(item => item.product_name?.toLowerCase().includes(search.toLowerCase()))
  );

  const StatusBadge = ({ status }) => {
    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="space-y-6 p-6" data-testid="stock-request-list">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Stock Requests</h1>
          <p className="text-sm text-gray-500">View and manage your stock requests to HQ</p>
        </div>
        <Button onClick={() => navigate('/warehouse/stock-requests/new')} className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-request-btn">
          <Plus className="w-4 h-4 mr-2" /> New Request
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by request number or product..."
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="pending">Pending Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchRequests}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Request Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No stock requests found</p>
              <Button variant="link" onClick={() => navigate('/warehouse/stock-requests/new')}>
                Create your first request
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Request #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Required By</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRequests.map(request => (
                  <TableRow key={request.id} className="hover:bg-gray-50">
                    <TableCell className="font-medium">{request.request_number}</TableCell>
                    <TableCell>{formatDate(request.created_at)}</TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">{request.total_items} items</span>
                        <span className="text-xs text-gray-500">
                          {request.items?.slice(0, 2).map(i => i.product_name).join(', ')}
                          {request.items?.length > 2 && '...'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {request.required_by_date ? formatDateOnly(request.required_by_date) : '-'}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={request.status} />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => navigate(`/warehouse/stock-requests/${request.id}`)} data-testid={`view-btn-${request.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        {request.status === 'draft' && (
                          <>
                            <Button variant="ghost" size="icon" onClick={() => handleSubmit(request.id)} title="Submit to HQ" data-testid={`submit-btn-${request.id}`}>
                              <Send className="w-4 h-4 text-green-600" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(request.id)} title="Cancel" data-testid={`delete-btn-${request.id}`}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </>
                        )}
                        {request.status === 'pending' && (
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(request.id)} title="Cancel Request" data-testid={`cancel-btn-${request.id}`}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {filteredRequests.length} of {pagination.total} requests
          </p>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
              disabled={pagination.page === 1}
            >
              Previous
            </Button>
            <Button 
              variant="outline"
              onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
              disabled={pagination.page === pagination.total_pages}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockRequestListPage;
